import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Dictionary } from '../services/api';
import { getMaxPairsInDictionary } from '../builtInDictionaries';
import ShareDictionaryForm from './ShareDictionaryForm';
import NotificationCenter from './NotificationCenter';
import AdminDashboard from './AdminDashboard';
import TeacherDashboard from './TeacherDashboard';
import StudentDashboard from './StudentDashboard';

interface UserDashboardProps {
  onSelectDictionary?: (dictionary: Dictionary) => void;
  onBackToGame?: () => void;
  onLogout?: () => void;
  onStartGame?: (dictionary: any, assignmentId?: number, gameId?: number, pairCount?: number) => void;
  initialRole?: 'teacher' | 'student' | null;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ onSelectDictionary, onBackToGame, onLogout, onStartGame, initialRole }) => {
  const { user, logout } = useAuth();
  const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareForm, setShowShareForm] = useState<{dictionaryId: number, dictionaryName: string} | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<{id: number, name: string} | null>(null);
  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState<string>('');

  // Role management
  const [userRoles, setUserRoles] = useState<{isTeacher: boolean; isStudent: boolean; hasBothRoles: boolean} | null>(null);
  const [activeRole, setActiveRole] = useState<'teacher' | 'student' | null>(initialRole || null);

  const loadUnreadCount = async () => {
    try {
      const response = await apiService.getUnreadNotificationCount();
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadDictionaries = async () => {
    try {
      const response = await apiService.getDictionaries();
      console.log('API Response:', response); // Debug log
      
      // Handle both formats: direct array or { dictionaries: [...] }
      const dicts = Array.isArray(response) ? response : ((response as any).dictionaries || []);
      setDictionaries(dicts);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Dictionary loading error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dictionaries';
      setError(errorMessage);
      setDictionaries([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadUserRoles = async () => {
    try {
      const roles = await apiService.getUserRoles();
      setUserRoles(roles);

      // Set default active role (only if initialRole wasn't provided)
      if (!initialRole) {
        if (roles.hasBothRoles) {
          // If has both roles, default to teacher
          setActiveRole('teacher');
        } else if (roles.isTeacher) {
          setActiveRole('teacher');
        } else if (roles.isStudent) {
          setActiveRole('student');
        }
      }
    } catch (error) {
      console.error('Failed to load user roles:', error);
    }
  };

  useEffect(() => {
    loadDictionaries();
    loadUnreadCount();
    loadUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin always gets AdminDashboard
  if (user?.role === 'admin') {
    return (
      <AdminDashboard
        onClose={() => onBackToGame && onBackToGame()}
        onSelectDictionary={onSelectDictionary}
      />
    );
  }

  // ONLY show teacher/student dashboards if initialRole is explicitly set to 'teacher' or 'student'
  // (from the separate panel buttons in main page)
  if (initialRole && userRoles) {
    // Handle role-based dashboard rendering
    // If user has both roles, show role selector and active dashboard
    if (userRoles.hasBothRoles && activeRole) {
      return (
        <div className="dual-role-wrapper">
          <div className="role-selector-bar">
            <button
              className={`role-select-btn ${activeRole === 'teacher' ? 'active' : ''}`}
              onClick={() => setActiveRole('teacher')}
            >
              👨‍🏫 Teacher View
            </button>
            <button
              className={`role-select-btn ${activeRole === 'student' ? 'active' : ''}`}
              onClick={() => setActiveRole('student')}
            >
              📚 Student View
            </button>
          </div>
          {activeRole === 'teacher' ? (
            <TeacherDashboard
              onClose={() => onBackToGame && onBackToGame()}
              onSelectDictionary={onSelectDictionary}
            />
          ) : (
            <StudentDashboard
              onClose={() => onBackToGame && onBackToGame()}
              onStartGame={(dictionary, assignmentId, gameId, pairCount) => {
                if (onStartGame) {
                  onStartGame(dictionary, assignmentId, gameId, pairCount);
                }
              }}
            />
          )}
        </div>
      );
    }

    // Single role: just teacher
    if (userRoles.isTeacher && !userRoles.isStudent) {
      return (
        <TeacherDashboard
          onClose={() => onBackToGame && onBackToGame()}
          onSelectDictionary={onSelectDictionary}
        />
      );
    }

    // Single role: just student
    if (userRoles.isStudent && !userRoles.isTeacher) {
      return (
        <StudentDashboard
          onClose={() => onBackToGame && onBackToGame()}
          onStartGame={(dictionary, assignmentId, gameId, pairCount) => {
            if (onStartGame) {
              onStartGame(dictionary, assignmentId, gameId, pairCount);
            }
          }}
        />
      );
    }
  }

  // If we get here, show the ORIGINAL account panel (dictionaries, logout, sharing, etc)
  // This happens when:
  // 1. User clicks their username (no initialRole set)
  // 2. User is not admin
  // 3. User doesn't have teacher/student roles, OR they do but didn't click the panel buttons

  const handleShareSuccess = () => {
    setShowShareForm(null);
    // You could show a success message here
  };

  const handleNotificationsOpen = () => {
    setShowNotifications(true);
    setUnreadCount(0); // Clear the counter when opened
  };

  const handleNotificationsClose = () => {
    setShowNotifications(false);
    loadUnreadCount(); // Refresh the count when closed
    loadDictionaries(); // Refresh dictionaries in case new ones were shared
  };

  const showDeleteConfirm = (id: number, name: string) => {
    setDeleteConfirm({ id, name });
  };

  const handleDeleteDictionary = async () => {
    if (!deleteConfirm) return;

    try {
      await apiService.deleteDictionary(deleteConfirm.id);
      setDictionaries(dicts => dicts.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete dictionary');
      setDeleteConfirm(null);
      console.error(err);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const startEditingName = (dictId: number, currentName: string) => {
    setEditingNameId(dictId);
    setEditingNameValue(currentName);
  };

  const cancelEditingName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const saveEditedName = async (dictId: number) => {
    if (!editingNameValue.trim()) {
      cancelEditingName();
      return;
    }

    try {
      await apiService.updateDictionary(dictId, { name: editingNameValue.trim() });
      // Update local state
      setDictionaries(dicts =>
        dicts.map(d => d.id === dictId ? { ...d, name: editingNameValue.trim() } : d)
      );
      setEditingNameId(null);
      setEditingNameValue('');
    } catch (err) {
      console.error('Failed to update dictionary name:', err);
      setError('Failed to update game name');
      cancelEditingName();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getItemCount = (data: Record<string, string> | any) => {
    if (!data || typeof data !== 'object') return 0;
    return getMaxPairsInDictionary(data);
  };

  const getDisplayName = (dict: Dictionary) => {
    // Just return the actual name - new AI dictionaries will have proper topic names
    return dict.name;
  };

  if (loading) {
    return <div className="dashboard">Loading your saved games...</div>;
  }

  if (!user) {
    return <div className="dashboard">Loading user information...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Welcome, {user?.username}!</h2>
        <div className="dashboard-actions">
          <button 
            onClick={handleNotificationsOpen} 
            className="back-button"
            style={{
              position: 'relative',
              backgroundColor: unreadCount > 0 ? '#ff6b6b' : undefined
            }}
            title={`${unreadCount} unread notifications`}
          >
            🔔 Notifications
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  fontWeight: 'bold'
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={onBackToGame} className="back-button">
            Back to Game
          </button>
          <button onClick={() => {
            logout();
            onLogout?.();
          }} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-stats">
        <h3>Account Information</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <strong>Account Type:</strong> {user?.role}
          </div>
          <div className="stat-item">
            <strong>Member Since:</strong> {user?.created_at ? formatDate(user.created_at) : (user?.createdAt ? formatDate(user.createdAt) : 'N/A')}
          </div>
          <div className="stat-item">
            <strong>Email:</strong> {user?.email}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dictionaries-section">
        <h3>Your Saved Games ({dictionaries.length})</h3>

        {dictionaries.length === 0 ? (
          <div className="empty-state">
            <p>You haven't saved any games yet.</p>
            <p>Play a game and save it to see it here!</p>
          </div>
        ) : (
          <div className="dictionaries-grid">
            {dictionaries.map(dict => (
              <div key={dict.id} className="dictionary-card">
                <div className="dictionary-header">
                  {editingNameId === dict.id ? (
                    <div className="name-edit-container">
                      <input
                        type="text"
                        className="name-edit-input"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        onBlur={() => saveEditedName(dict.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            saveEditedName(dict.id);
                          }
                          if (e.key === 'Escape') {
                            cancelEditingName();
                          }
                        }}
                        autoFocus
                        maxLength={100}
                      />
                    </div>
                  ) : (
                    <div className="name-display-container">
                      <h4>{getDisplayName(dict)}</h4>
                      <button
                        className="edit-name-btn"
                        onClick={() => startEditingName(dict.id, getDisplayName(dict))}
                        title="Edit game name"
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                  <span className="dictionary-visibility">
                    {dict.is_public ? '🌐 Public' : '🔒 Private'}
                  </span>
                </div>
                <div className="dictionary-info">
                  <p>{getItemCount(dict.data)} word matches</p>
                  <p className="dictionary-date">Created: {formatDate(dict.created_at)}</p>
                  {dict.description && <p className="dictionary-description">{dict.description}</p>}
                </div>
                <div className="dictionary-actions">
                  <button 
                    onClick={() => onSelectDictionary?.(dict)}
                    className="play-button"
                  >
                    Play Game
                  </button>
                  {!dict.is_public && (
                    <button 
                      onClick={() => setShowShareForm({dictionaryId: dict.id, dictionaryName: dict.name})}
                      className="share-button"
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        margin: '0 4px'
                      }}
                    >
                      📤 Share
                    </button>
                  )}
                  <button 
                    onClick={() => showDeleteConfirm(dict.id, getDisplayName(dict))}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share Dictionary Modal */}
      {showShareForm && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <ShareDictionaryForm
                dictionaryId={showShareForm.dictionaryId}
                dictionaryName={showShareForm.dictionaryName}
                onSuccess={handleShareSuccess}
                onCancel={() => setShowShareForm(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Notification Center Modal */}
      {deleteConfirm && (
        <div className="confirm-popup-overlay">
          <div className="confirm-popup">
            <h3>Delete Game</h3>
            <p>Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="confirm-popup-actions">
              <button className="cancel-btn" onClick={cancelDelete}>Cancel</button>
              <button className="delete-btn" onClick={handleDeleteDictionary}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showNotifications && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <NotificationCenter onClose={handleNotificationsClose} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;